import type { MessageGeneration } from '../types/chat';

/** Client-side message send: ISO without trailing Z, ms padded to 6 digits (backend-style). */
export function formatShowMessageTimeForSend(): string {
  return new Date().toISOString().replace('Z', '').replace(/\.(\d{3})/, (_, ms) => `.${ms}000`);
}

/** Preserve backend value as string, or normalize numeric timestamps to the same string shape. */
export function showMessageTimeFromBackend(createdDate: unknown): string | undefined {
  if (createdDate == null || createdDate === '') {
    return undefined;
  }
  if (typeof createdDate === 'string') {
    const trimmed = createdDate.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof createdDate === 'number' && Number.isFinite(createdDate)) {
    const ms = createdDate < 1_000_000_000_000 ? createdDate * 1000 : createdDate;
    return new Date(ms).toISOString().replace('Z', '').replace(/\.(\d{3})/, (_, frac) => `.${frac}000`);
  }
  return undefined;
}

/**
 * Parses `showMessageTime` (often no timezone suffix) or falls back to `createdDate` for `Date` APIs.
 */
export function parseMessageTimeForDisplay(
  showMessageTime?: string,
  createdDate?: string | number
): Date | null {
  if (showMessageTime && showMessageTime.trim()) {
    const s = showMessageTime.trim();
    const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
    const d = new Date(hasTz ? s : `${s}Z`);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }
  }
  if (createdDate != null && createdDate !== '') {
    const d = new Date(createdDate as string | number);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }
  }
  return null;
}

export function timestampFromBackendCreatedDate(createdDate: unknown): number {
  const show = showMessageTimeFromBackend(createdDate);
  const parsed = parseMessageTimeForDisplay(
    show,
    typeof createdDate === 'string' || typeof createdDate === 'number' ? createdDate : undefined
  );
  return parsed ? parsed.getTime() : Date.now();
}

export function messageGenerationTimeMs(
  generation?: Pick<MessageGeneration, 'showMessageTime' | 'createdDate'> | null
): number {
  if (!generation) {
    return Date.now();
  }
  return (
    parseMessageTimeForDisplay(generation.showMessageTime, generation.createdDate)?.getTime() ??
    Date.now()
  );
}
